import React, { useRef, useEffect, useState, useCallback } from 'react';

import { Spin, Card, Space, Radio, Modal, notification } from 'antd';

import { PDFDocument, PDFTextField, PDFCheckBox, PDFRadioGroup, PDFOptionList, PDFDropdown } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';

import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import * as pdfjsViewer from 'pdfjs-dist/web/pdf_viewer';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';

import SignatureOnPdf from './signature-on-pdf';

import { DejaVuSans } from '../../constants';

import { isArray } from 'lodash';

const DEFAULT_SCALE_VALUE = 'page-width';

let timeoutId;

export const PdfEditorComponent = ({
  fileBytes,
  clearFieldNames,
  fieldsToFill,
  registerFormSaveCallback,
  registerFormClearCallback,
  rootContainer,
  showSignatureOnPdf,
  loading,
  registerSignatureSaveCallback,
  signatureBytes,
  onFormEditorClick,
  onPdfLoadedEvent,
}) => {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [signatureParams, setSignatureParams] = useState();
  const [pdfViewer, setPdfViewer] = useState();
  const [hasOverflowingChildren, setHasOverflowingChildren] = useState(false);
  const [pdfScaleValue, setPdfScaleValue] = useState();

  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  const canvasRef = useRef();
  const viewerRef = useRef();
  const pdfDocumentRef = useRef();

  useEffect(() => {
    if (typeof registerFormSaveCallback === 'function' && pdfViewer && pdfViewer.pdfDocument) {
      registerFormSaveCallback(getPdfData);
    }
  }, [pdfViewer, registerFormSaveCallback, getPdfData, pdfLoading]);

  useEffect(() => {
    if (typeof registerFormClearCallback === 'function' && pdfViewer && pdfViewer.pdfDocument) {
      registerFormClearCallback(clearFormFields);
    }
  }, [pdfViewer, registerFormClearCallback, clearFormFields, pdfLoading]);

  useEffect(() => {
    if (typeof registerSignatureSaveCallback === 'function' && pdfViewer && pdfViewer.pdfDocument) {
      registerSignatureSaveCallback(onSignatureSaveCallback);
    }
  }, [pdfViewer, registerSignatureSaveCallback, signatureParams, onSignatureSaveCallback, pdfLoading]);

  useEffect(() => {
    const load = async () => {
      try {
        let newFileBytes = fileBytes;
        if (!newFileBytes && pdfViewer && pdfViewer.pdfDocument) {
          setPdfLoading(true);
          newFileBytes = await pdfViewer.pdfDocument.saveDocument();
        }

        if (newFileBytes) {
          setPdfLoading(true);
          let pdfBytes = await loadAndPrefillDocument({ fileBytes: newFileBytes });
          await renderPdf(pdfBytes);
        }
      } catch (error) {
        console.error(error);
        Modal.error({
          getContainer: rootContainer,
          title: 'An error occurred while opening the document',
        });
      }
    };

    load();
  }, [fileBytes, fieldsToFill]);

  useEffect(() => {
    if (pdfViewer && pdfViewer.pdfDocument) {
      onSizeChange({ target: { value: pdfScaleValue ?? DEFAULT_SCALE_VALUE } });
    }
  }, [pdfViewer, pdfLoading]);

  useEffect(() => {
    if (pdfViewer && pdfViewer.pdfDocument && !pdfLoading) {
      const totalPages = pdfViewer.pdfDocument.numPages;

      for (let i = 1; i <= totalPages; i++) {
        pdfViewer.pdfDocument.getPage(i).then((page) => {
          page.getAnnotations().then((annotations) => {
            annotations.forEach((annotation) => {
              const fieldName = annotation.fieldName;

              const formField = pdfDocumentRef.current?.querySelector(`[name="${fieldName}"]`);
              if (formField) {
                formField.title = annotation.alternativeText;
              }
            });
          });
        });
      }
    }
  }, [pdfViewer, pdfLoading]);

  const getPdfData = useCallback(() => pdfViewer.pdfDocument.saveDocument(), [pdfViewer, pdfLoading]);

  const clearFormFields = useCallback(
    (callback) => {
      pdfViewer.pdfDocument
        .saveDocument()
        .then((fileBytesFromViewer) => loadAndPrefillDocument({ fileBytes: fileBytesFromViewer, isClear: true }))
        .then((result) => renderPdf(result, undefined, callback));
    },
    [pdfViewer, loadAndPrefillDocument, renderPdf, pdfLoading],
  );

  const loadAndPrefillDocument = async ({ fileBytes, signature, isClear }) => {
    setPdfLoading(true);

    const pdfDoc = await PDFDocument.load(fileBytes);

    pdfDoc.registerFontkit(fontkit);

    const customFont = await pdfDoc.embedFont(DejaVuSans);

    if (signature) {
      await drawImage(signature, pdfDoc);
    }

    let form = pdfDoc.getForm();

    if (form) {
      const formFields = form.getFields();
      formFields.forEach((formField) => {
        formField.disableReadOnly();
        const name = formField.getName();

        validateFormFieldTypes(formField, name, customFont);

        if (isClear === true) {
          clearFormFieldValue(formField, name, customFont);
        }

        if (formField instanceof PDFTextField) {
          formField.updateAppearances(customFont);
        }
      });

      if (isClear !== true) {
        // prefill server generated form data
        try {
          // clear fields before set new value
          if (isArray(clearFieldNames)) {
            clearFieldNames.forEach((fieldName) => {
              const pdfFormField = getFormField(form, fieldName);

              if (pdfFormField) {
                clearFormFieldValue(pdfFormField, fieldName, customFont);
              }
            });
          }

          // set generated new value
          if (isArray(fieldsToFill)) {
            fieldsToFill.forEach((fieldObject) => {
              const pdfFormField = getFormField(form, fieldObject.fieldName);

              if (pdfFormField) {
                setFormFieldValue(pdfFormField, fieldObject.fieldValue, fieldObject.fieldName, customFont);
              }
            });
          }
        } catch (err) {
          console.log(`Error occurred while setting value for PDF Form`);
          (console.error || console.log).call(console, err.stack || err);

          notification.error({
            message: 'Error',
            top: 55,
            duration: 10,
            getContainer: rootContainer,
            description: `Error occurred while setting value for PDF Form`,
          });
        }
      }
    }

    return await pdfDoc.save();
  };

  const renderPdf = async (fileBytes, scrollProps, callback) => {
    setPdfLoading(true);

    if (canvasRef.current) {
      const eventBus = new pdfjsViewer.EventBus();
      const pdfLinkService = new pdfjsViewer.PDFLinkService({ eventBus });
      const pdfViewer = new pdfjsViewer.PDFViewer({
        container: canvasRef.current,
        eventBus: eventBus,
        linkService: pdfLinkService,
        textLayerFactory: new pdfjsViewer.DefaultTextLayerFactory(),
        annotationLayerFactory: new pdfjsViewer.DefaultAnnotationLayerFactory(),
      });

      pdfLinkService.setViewer(pdfViewer);

      const onPagesLoaded = (event) => {
        if (timeoutId) clearTimeout(timeoutId);

        timeoutId = setTimeout(() => {
          setPdfLoading(false);

          if (typeof onPdfLoadedEvent == 'function') {
            onPdfLoadedEvent();
          }

          if (scrollProps) {
            pdfDocumentRef.current?.scrollTo(scrollProps.left, scrollProps.top);
          }

          addDeleteButtons(pdfViewer);

          if (typeof callback === 'function') {
            callback();
          }

          timeoutId = null;
        }, 400);
      };

      pdfViewer.eventBus.on('annotationlayerrendered', onPagesLoaded);

      setPdfViewer(pdfViewer);

      const newPdfDocument = await pdfjsLib.getDocument(fileBytes).promise;

      pdfViewer.setDocument(newPdfDocument);

      pdfLinkService.setDocument(newPdfDocument);
    }
  };

  const getFormField = (form, fieldName) => {
    try {
      return form.getField(fieldName);
    } catch (err) {
      console.log(`Error occurred while getting field - '${fieldName}'`);
      (console.error || console.log).call(console, err.stack || err);

      notification.error({
        message: 'Error',
        top: 55,
        duration: 10,
        getContainer: rootContainer,
        description: `Error occurred while getting field - '${fieldName}'`,
      });
    }
  };

  const setFormFieldValue = (formField, fieldValue, fieldName, customFont) => {
    try {
      if (formField instanceof PDFTextField) {
        if (typeof fieldValue == 'string') {
          formField.setText(fieldValue ?? '');
          formField.updateAppearances(customFont);
        } else if (typeof fieldValue == 'boolean') {
          formField.setText(fieldValue ? 'Y' : 'N');
          formField.updateAppearances(customFont);
        } else if (typeof fieldValue == 'number') {
          formField.setText(fieldValue.toString());
          formField.updateAppearances(customFont);
        } else {
          formField.setText(fieldValue ?? '');
          formField.updateAppearances(customFont);
        }
      } else if (formField instanceof PDFCheckBox) {
        if (
          fieldValue === true ||
          fieldValue === 1 ||
          (typeof fieldValue == 'string' &&
            (fieldValue.toLowerCase() == 'true' ||
              fieldValue.toLowerCase() == 't' ||
              fieldValue.toLowerCase() == '1' ||
              fieldValue.toLowerCase() == 'yes' ||
              fieldValue.toLowerCase() == 'y'))
        ) {
          formField.check();
        } else {
          formField.uncheck();
        }
      } else if (formField instanceof PDFRadioGroup) {
        formField.select(fieldValue);
      } else if (formField instanceof PDFOptionList) {
        const options = formField.getOptions();

        if (!isArray(options) || !options.includes(fieldValue)) {
          formField.addOptions(fieldValue);
        }

        formField.select(fieldValue);
      } else if (formField instanceof PDFDropdown) {
        const options = formField.getOptions();

        if (!isArray(options) || !options.includes(fieldValue)) {
          formField.addOptions(fieldValue);
        }

        formField.select(fieldValue);
      } else {
        console.log(`The field - '${fieldName}' has unknown type.`);

        notification.error({
          message: 'Error',
          top: 55,
          duration: 10,
          getContainer: rootContainer,
          description: `The field - '${fieldName}' has unknown type`,
        });
      }
    } catch (err) {
      console.log(`Error occurred while setting value - '${fieldValue}' for field - '${fieldName}'`);
      (console.error || console.log).call(console, err.stack || err);

      notification.error({
        message: 'Error',
        top: 55,
        duration: 10,
        getContainer: rootContainer,
        description: `Error occurred while setting value - '${fieldValue}' for field - '${fieldName}'`,
      });
    }
  };

  const validateFormFieldTypes = (formField, fieldName, customFont) => {
    try {
      if (formField instanceof PDFTextField) {
        formField.getText();
        formField.setMaxLength(undefined);
        formField.updateAppearances(customFont);
      } else if (formField instanceof PDFCheckBox) {
        formField.isChecked();
      } else if (formField instanceof PDFRadioGroup) {
        formField.getOptions();
        formField.getSelected();
      } else if (formField instanceof PDFOptionList) {
        formField.getOptions();
        formField.getSelected();
      } else if (formField instanceof PDFDropdown) {
        formField.getOptions();
        formField.getSelected();
      } else {
        console.log(`The field - '${fieldName}' has unknown type.`);

        notification.error({
          message: 'Error',
          top: 55,
          duration: 10,
          getContainer: rootContainer,
          description: `The field - '${fieldName}' has unknown type`,
        });
      }
    } catch (err) {
      clearFormFieldValue(formField, fieldName, customFont);
    }
  };

  const clearFormFieldValue = (formField, fieldName, customFont) => {
    try {
      if (formField instanceof PDFTextField) {
        formField.setText('');
        formField.updateAppearances(customFont);
      } else if (formField instanceof PDFCheckBox) {
        formField.uncheck();
      } else if (formField instanceof PDFRadioGroup) {
        formField.clear();
      } else if (formField instanceof PDFOptionList) {
        formField.clear();
      } else if (formField instanceof PDFDropdown) {
        formField.clear();
      } else {
        console.log(`The field - '${fieldName}' has unknown type.`);

        notification.error({
          message: 'Error',
          top: 55,
          duration: 10,
          getContainer: rootContainer,
          description: `The field - '${fieldName}' has unknown type`,
        });
      }
    } catch (err) {
      console.log(`Error occurred while clear field - '${fieldName}'`);
      (console.error || console.log).call(console, err.stack || err);

      notification.error({
        message: 'Error',
        top: 55,
        duration: 10,
        getContainer: rootContainer,
        description: `Error occurred while clear field - '${fieldName}'`,
      });
    }
  };

  const drawImage = async (signature, pdfDoc) => {
    signature.signaturePosition.x -= 20;

    const scrollTop = canvasRef.current.scrollTop;
    let y = signature.signaturePosition.y + scrollTop;
    let pageParams = getRelativePage(y);

    signature.signaturePosition.y -= 20 * (pageParams.pageNumber + 1);

    y = signature.signaturePosition.y + scrollTop;
    pageParams = getRelativePage(y);

    const relativeY = y - pageParams.previousHeight;
    const viewerRefPage = viewerRef.current.children[pageParams.pageNumber];
    const page = pdfDoc.getPage(pageParams.pageNumber);

    const imageSize = {
      width: (page.getWidth() * signature.signatureSize.width) / viewerRefPage.offsetWidth,
      height: (page.getHeight() * signature.signatureSize.height) / viewerRefPage.offsetHeight,
    };

    const imagePosition = {
      x: (page.getWidth() * signature.signaturePosition.x) / viewerRefPage.offsetWidth,
      y: (page.getHeight() * (viewerRefPage.offsetHeight - relativeY)) / viewerRefPage.offsetHeight - imageSize.height,
    };

    const pngImage = await pdfDoc.embedPng(signature.bytes);

    page.drawImage(pngImage, {
      x: imagePosition.x,
      y: imagePosition.y,
      width: imageSize.width,
      height: imageSize.height,
    });
  };

  const getRelativePage = (y) => {
    let height = 0;
    let pageNumber = 0;
    let previousHeight = 0;

    for (let element of Array.from(viewerRef.current.children)) {
      height += element.offsetHeight;

      if (y <= height) {
        break;
      } else {
        pageNumber++;
        previousHeight = height;
      }
    }

    return { pageNumber, previousHeight };
  };

  const onSignatureSaveCallback = useCallback(() => {
    if (!pdfLoading) {
      const scrollLeft = pdfDocumentRef.current?.scrollLeft;
      const scrollTop = pdfDocumentRef.current?.scrollTop;

      pdfViewer.pdfDocument
        .saveDocument()
        .then((x) => loadAndPrefillDocument({ fileBytes: x, signature: signatureParams }))
        .then((x) => renderPdf(x, { left: scrollLeft, top: scrollTop }));
    }
  }, [pdfLoading, pdfViewer, signatureParams]);

  const onSizeChange = async (event) => {
    if (DEFAULT_SCALE_VALUE === event.target.value) {
      const page = await pdfViewer.pdfDocument.getPage(1);
      pdfViewer.currentScaleValue = pdfDocumentRef.current.offsetWidth / (page.view[2] + page.view[2] * 0.42);
    } else {
      pdfViewer.currentScaleValue = event.target.value;
    }

    setPdfScaleValue(event.target.value);

    setHasOverflowingChildren(pdfDocumentRef?.current?.offsetWidth < pdfDocumentRef?.current?.scrollWidth);
  };

  const addDeleteButtons = (pdfViewer) => {
    if (!pdfDocumentRef.current) return;

    const pages = pdfDocumentRef.current.querySelectorAll('.page');

    pages.forEach((page) => {
      const pageNumber = page.getAttribute('data-page-number');
      if (!pageNumber) return;

      if (page.querySelector('.delete-button')) return;

      const deleteButton = document.createElement('button');
      deleteButton.innerText = 'Delete';
      deleteButton.classList.add('delete-button');
      deleteButton.setAttribute('type', 'button');

      Object.assign(deleteButton.style, {
        position: 'absolute',
        top: '10px',
        right: '10px',
        background: 'red',
        color: 'white',
        border: 'none',
        padding: '5px 10px',
        cursor: 'pointer',
        zIndex: '10',
      });

      deleteButton.addEventListener('click', (event) => {
        event.preventDefault();
        deletePage(Number(pageNumber) - 1, pdfViewer);
      });

      page.style.position = 'relative';

      page.appendChild(deleteButton);
    });
  };

  const deletePage = async (pageNumber, pdfViewer) => {
    if (!pdfViewer.pdfDocument) return;

    const fileBytes = await pdfViewer.pdfDocument.saveDocument();

    const pdfDoc = await PDFDocument.load(fileBytes);

    pdfDoc.removePage(pageNumber);

    const newFileBytes = await pdfDoc.save();

    await renderPdf(newFileBytes);
  };

  return (
    <Spin spinning={loading || pdfLoading} className="pdf-editor">
      <Card
        title={null}
        size="small"
        bodyStyle={{
          padding: '5px',
        }}
      >
        <Space size="small">
          <Radio.Group id="pdf_editor_scale_buttons" defaultValue={DEFAULT_SCALE_VALUE} size="small" onChange={onSizeChange}>
            <Radio.Button id="pdf_editor_scale_fit_button" value={DEFAULT_SCALE_VALUE}>
              Fit
            </Radio.Button>
            <Radio.Button id="pdf_editor_scale_50_button" value={0.5}>
              50%
            </Radio.Button>
            <Radio.Button id="pdf_editor_scale_100_button" value={1}>
              100%
            </Radio.Button>
            <Radio.Button id="pdf_editor_scale_150_button" value={1.5}>
              150%
            </Radio.Button>
            <Radio.Button id="pdf_editor_scale_150_button" value={2}>
              200%
            </Radio.Button>
          </Radio.Group>
        </Space>
      </Card>

      <Card
        title={null}
        bodyStyle={{
          padding: 0,
        }}
      >
        <div
          id="pdfDocument"
          ref={pdfDocumentRef}
          style={{
            position: 'relative',
            overflow: 'auto',
            height: '900px',
            padding: 0,
            display: 'flex',
            justifyContent: hasOverflowingChildren ? 'unset' : 'center',
          }}
          onClick={onFormEditorClick}
        >
          <div
            id="viewerContainer"
            ref={canvasRef}
            style={{
              position: 'absolute',
            }}
          >
            <div id="viewer" className="pdfViewer" ref={viewerRef}></div>

            {showSignatureOnPdf ? (
              <SignatureOnPdf
                rootContainer={rootContainer}
                setSignatureParams={setSignatureParams}
                signatureBytes={signatureBytes}
                defaultPosition={{ x: pdfDocumentRef.current.scrollLeft ?? 0, y: pdfDocumentRef.current.scrollTop ?? 0 }}
              />
            ) : null}
          </div>
        </div>
      </Card>
    </Spin>
  );
};
